import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useAudioStore } from "../../stores/audioStore";
import { useHighlightStore } from "../../stores/highlightStore";
import "../../styles/MainContent.css";
import {
  processContentWithBlocks,
  validateBlockStructure,
} from "../../utils/blockProcessor";
import { extractBodyContent, isCompleteHtmlDocument } from "../../utils/helpers";
import { createHighlighter } from "../../utils/webHighlightUtils";
import { LoadingSpinner } from "../ui";

const formatTime = (timeInSeconds, showRemaining = false, duration = 0) => {
  if (isNaN(timeInSeconds) || timeInSeconds < 0) return "0:00";
  const time = showRemaining ? duration - timeInSeconds : timeInSeconds;
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  const prefix = showRemaining && time > 0 ? "-" : "";
  return `${prefix}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const MainContent = memo(({ topic, textSize }) => {
  // Refs for highlighting
  const contentRef = useRef(null);
  const highlighterRef = useRef(null);
  // Track last synced highlights using their frontend IDs (web-highlighter's internal IDs)
  const lastSyncedHighlights = useRef(new Set());

  // Highlighting store
  const addHighlight = useHighlightStore((state) => state.addHighlight);
  const getHighlights = useHighlightStore((state) => state.getHighlights);
  const fetchHighlights = useHighlightStore((state) => state.fetchHighlights);
  const setHighlighter = useHighlightStore((state) => state.setHighlighter);
  const setLoading = useHighlightStore((state) => state.setLoading);
  const setError = useHighlightStore((state) => state.setError);
  const deleteHighlight = useHighlightStore((state) => state.delete);

  // Subscribe to highlights state for DOM synchronization
  const highlights = useHighlightStore((state) => state.highlights);

  // Enhanced audio store usage with error handling
  const isPlaying = useAudioStore((state) => state.isPlaying);
  const currentTime = useAudioStore((state) => state.currentTime);
  const duration = useAudioStore((state) => state.duration);
  const playlist = useAudioStore((state) => state.playlist);
  const trackIndex = useAudioStore((state) => state.trackIndex);
  const audioState = useAudioStore((state) => state.audioState);
  const error = useAudioStore((state) => state.error);
  const isLoading = useAudioStore((state) => state.isLoading);
  const buffered = useAudioStore((state) => state.buffered);
  const play = useAudioStore((state) => state.play);
  const pause = useAudioStore((state) => state.pause);
  const seek = useAudioStore((state) => state.seek);
  const nextTrack = useAudioStore((state) => state.nextTrack);
  const prevTrack = useAudioStore((state) => state.prevTrack);
  const hasNextTrack = useAudioStore((state) => state.hasNextTrack);
  const hasPrevTrack = useAudioStore((state) => state.hasPrevTrack);
  const clearError = useAudioStore((state) => state.clearError);

  // Error handling for component-level errors
  const { addError } = useErrorHandler();

  const currentTrack = playlist[trackIndex] || {};
  const hasAudio = !!currentTrack?.audioSrc;
  const hasMultipleTracks = playlist.length > 1;

  // Simple hash function for content identification (not used for robust page ID)
  const createSimpleHash = useCallback((str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substring(0, 8);
  }, []);

  // Create robust page ID that uniquely identifies this specific page (content-independent)
  const createRobustPageId = useCallback((topic) => {
    if (!topic) return "default";

    const baseId = topic.id || "unknown";
    const realTopicId = topic.realTopicId || "unknown";
    const contentHeading = topic.content?.heading || "no-heading";

    const originalStructure = topic.originalStructure || {};
    const pageNumber = originalStructure.pageNumber || 1;
    const subTopicId = originalStructure.subTopicId || "unknown";

    const robustId = `${baseId}__${realTopicId}__${subTopicId}__page${pageNumber}`;

    return robustId;
  }, []);

  // Callback to get current selected color directly from store (no subscription)
  const getCurrentColor = useCallback(() => {
    return useHighlightStore.getState().selectedColor;
  }, []);

  // **IMPORTANT: Define syncHighlightsWithDOM first, as other callbacks depend on it.**
  // Enhanced DOM sync function
  const syncHighlightsWithDOM = useCallback((topicId, pageId, forceSync = false) => {
    if (!highlighterRef.current || !contentRef.current) {
      console.warn('⚠️ Cannot sync highlights - missing highlighter or content ref');
      return;
    }

    const currentPageHighlights = getHighlights(topicId, pageId);
    // When syncing, we care about the frontend IDs that web-highlighter uses in the DOM
    const currentHighlightFrontendIds = new Set(currentPageHighlights.map(h => h.highlightData?.id).filter(Boolean));

    // Check if sync is needed based on frontend IDs
    const lastSynced = lastSyncedHighlights.current;
    const hasChanges = forceSync ||
      currentHighlightFrontendIds.size !== lastSynced.size ||
      [...currentHighlightFrontendIds].some(id => !lastSynced.has(id)) ||
      [...lastSynced].some(id => !currentHighlightFrontendIds.has(id));

    if (!hasChanges) {
      console.log('🔄 No highlight changes detected, skipping DOM sync');
      return;
    }

    console.log(`🔄 Syncing highlights: ${currentPageHighlights.length} in store for (Topic: ${topicId}, Page: ${pageId})`);

    try {
      // Clear all existing highlights from DOM first using web-highlighter's removeAll
      // This is generally safer than manual DOM manipulation for highlight spans.
      highlighterRef.current.removeAll();

      // Re-apply all highlights from store
      if (currentPageHighlights.length > 0) {
        console.log(`🔄 Restoring ${currentPageHighlights.length} highlights to DOM via deserialization`);
        // The deserialize method in WebHighlighterWrapper will now handle extracting
        // the frontend ID from `highlight.highlightData.id` for web-highlighter's use.
        highlighterRef.current.deserialize(currentPageHighlights);
      }

      // Update last synced state with the frontend IDs that are now in the DOM
      lastSyncedHighlights.current = new Set(currentHighlightFrontendIds);

      console.log('✅ Highlight DOM sync completed successfully');
    } catch (error) {
      console.error('❌ Failed to sync highlights with DOM:', error);
      setError("Failed to sync highlights");
    }
  }, [getHighlights, setError]); // Dependencies of syncHighlightsWithDOM

  // Highlighting event handlers
  const handleHighlightClick = useCallback((highlightData) => {
    // highlightData.id here is the frontend ID from web-highlighter
    console.log("Highlight clicked:", highlightData.id);
  }, []);

  // Callback for when a user creates a new selection/highlight in the DOM
  const handleSelectionCreate = useCallback(
    async (data, inst) => {
      if (!topic?.id) {
        console.warn("⚠️ Cannot create highlight: Topic ID is missing.");
        return;
      }

      try {
        setLoading(true);

        const topicId = topic.realTopicId || topic.id;
        const pageId = createRobustPageId(topic);
        const sources = data.sources || []; // `sources` contains web-highlighter's generated data

        if (sources.length === 0) {
          return;
        }

        const newHighlightBackendIds = [];

        for (const source of sources) {
          const highlightPayload = {
            ...source, // This contains web-highlighter's ID as `id`
            topicId,
            pageId,
            createdAt: new Date().toISOString(),
          };

          try {
            const savedHighlight = await addHighlight(topicId, pageId, highlightPayload);

            if (savedHighlight?.id) {
              newHighlightBackendIds.push(savedHighlight.id); // Track backend ID
              console.log(`✅ Highlight saved to store. Backend ID: ${savedHighlight.id}, Frontend ID: ${savedHighlight.highlightData?.id}`);
            }
          } catch (error) {
            console.error("Failed to add highlight to store:", error);
          }
        }

        if (newHighlightBackendIds.length > 0) {
          setTimeout(() => {
            syncHighlightsWithDOM(topicId, pageId, true);
          }, 10);
        }

      } catch (error) {
        console.error("Failed to create highlight:", error);
        setError("Failed to create highlight");
        addError("Failed to create highlight");
      } finally {
        setLoading(false);
      }
    },
    [topic, addHighlight, setLoading, setError, addError, createRobustPageId, syncHighlightsWithDOM]
  );

  // Callback to handle highlight removal (triggered by WebHighlighterWrapper)
  const handleHighlightRemove = useCallback(
    (frontendHighlightIds) => {
      if (!Array.isArray(frontendHighlightIds) || frontendHighlightIds.length === 0) return;

      console.log(`🗑️ Received request to remove ${frontendHighlightIds.length} highlights with frontend IDs:`, frontendHighlightIds);

      frontendHighlightIds.forEach((frontendId) => {
        if (frontendId) {
          deleteHighlight(frontendId);
        }
      });

      if (topic?.id) {
        const topicId = topic.realTopicId || topic.id;
        const pageId = createRobustPageId(topic);
        setTimeout(() => {
          syncHighlightsWithDOM(topicId, pageId, true);
        }, 50);
      }
    },
    [deleteHighlight, topic, createRobustPageId, syncHighlightsWithDOM]
  );

  // Initialize highlighter when content is ready or topic changes
  useEffect(() => {
    console.log("🔍 Highlighter useEffect triggered");

    if (!contentRef.current || !topic?.id) {
      console.warn(
        "⚠️ Missing content ref or topic ID, skipping highlighter initialization"
      );
      return;
    }

    if (highlighterRef.current) {
      console.log("🧹 Cleaning up previous highlighter");
      highlighterRef.current.stop();
      highlighterRef.current = null;
    }

    lastSyncedHighlights.current = new Set();

    if (contentRef.current) {
      const existingHighlightSpans =
        contentRef.current.querySelectorAll(".web-highlight-wrap, .rangy-highlight");
      existingHighlightSpans.forEach((span) => {
        const parent = span.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(span.textContent), span);
          parent.normalize();
        }
      });
    }

    const initializeHighlighter = async () => {
      try {
        const highlighter = await createHighlighter(
          contentRef.current,
          handleHighlightClick,
          handleSelectionCreate,
          getCurrentColor,
          handleHighlightRemove
        );

        if (highlighter) {
          highlighterRef.current = highlighter;
          setHighlighter(highlighter);

          highlighter.run();

          const topicId = topic.realTopicId || topic.id;
          const pageId = createRobustPageId(topic);

          try {
            await fetchHighlights(topicId, pageId);
            setTimeout(() => {
              syncHighlightsWithDOM(topicId, pageId, true);
            }, 100);
          } catch (error) {
            console.error("❌ Failed to fetch highlights from API:", error);
            setTimeout(() => {
              syncHighlightsWithDOM(topicId, pageId, true);
            }, 100);
          }
        }
      } catch (error) {
        console.error("❌ Failed to initialize highlighter:", error);
        setError("Failed to initialize highlighting");
      }
    };

    const timeoutId = setTimeout(initializeHighlighter, 100);

    return () => {
      clearTimeout(timeoutId);
      if (highlighterRef.current) {
        console.log("🧹 Cleaning up highlighter on unmount/re-init...");
        highlighterRef.current.stop();
        highlighterRef.current = null;
      }
      lastSyncedHighlights.current = new Set();
    };
  }, [
    topic?.id,
    topic?.realTopicId,
    topic?.originalStructure?.pageId,
    handleHighlightClick,
    handleSelectionCreate,
    handleHighlightRemove,
    setHighlighter,
    fetchHighlights,
    syncHighlightsWithDOM,
    createRobustPageId,
    getCurrentColor
  ]);

  // Effect to sync DOM highlights with store state changes
  useEffect(() => {
    if (!highlighterRef.current || !topic?.id) return;

    const topicId = topic.realTopicId || topic.id;
    const pageId = createRobustPageId(topic);

    const timeoutId = setTimeout(() => {
      syncHighlightsWithDOM(topicId, pageId, false);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [highlights, topic?.id, topic?.realTopicId, topic?.originalStructure?.pageId, syncHighlightsWithDOM, createRobustPageId]);

  // Block-based content processing for resilient highlighting
  const getContentHtml = useCallback(() => {
    if (topic?.content && typeof topic.content === 'string') {
      console.log('🔄 Processing direct HTML content for topic:', topic.id);
      return isCompleteHtmlDocument(topic.content) ? extractBodyContent(topic.content) : topic.content;
    }

    if (!topic?.content?.body || topic.content.body.length === 0) {
      console.log('❌ No content body found, returning empty string');
      return "";
    }

    console.log(
      "🔄 Processing structured content with stable blocks for topic:",
      topic.id
    );

    const topicId = topic.realTopicId || topic.id || "default";
    const pageId = createRobustPageId(topic);

    console.log(
      `📄 Page-specific processing: topicId="${topicId}", pageId="${pageId}"`
    );

    const processedContent = processContentWithBlocks(
      topic.content.body,
      topicId,
      pageId
    );

    validateBlockStructure(processedContent);

    return processedContent;
  }, [topic?.content, topic?.id, createRobustPageId]);

  // Memoize the content HTML to prevent unnecessary re-renders
  const contentHtml = useMemo(() => getContentHtml(), [getContentHtml]);

  // Handle progress bar click with enhanced error handling
  const handleProgressBarClick = useCallback(
    (event) => {
      if (!hasAudio || !duration) return;

      try {
        const progressBar = event.currentTarget;
        const rect = progressBar.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, clickX / rect.width));
        const newTime = percentage * duration;

        seek(newTime);
      } catch {
        addError("Failed to seek audio");
      }
    },
    [hasAudio, duration, seek, addError]
  );

  const handlePlayPause = useCallback(async () => {
    if (!hasAudio) return;

    try {
      if (error) {
        clearError();
      }

      if (isPlaying) {
        pause();
      } else {
        await play();
      }
    } catch {
      addError("Audio playback failed");
    }
  }, [hasAudio, isPlaying, play, pause, error, clearError, addError]);

  if (!topic) {
    return (
      <main className="main-content-panel">
        <LoadingSpinner size="large" message="Loading content..." />
      </main>
    );
  }

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <main className={`main-content-panel text-size-${textSize}`}>
      <article className="lesson-content">
        <div className="lesson-content-body" ref={contentRef}>
          {contentHtml ? (
            <div
              className="html-content"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          ) : (
            <div className="no-content">
              <p>No content available for this topic.</p>
            </div>
          )}
        </div>
      </article>
    </main>
  );
});

export default MainContent;